// ファイルアップロード用のカスタムフック

import { useState, useCallback } from 'react';
import { UploadedFile, FileValidationError, FileUploadConfig, DEFAULT_FILE_CONFIG } from '../types';
import { validateFilesWithContext, generateFileId, ValidationContext } from '../utils/fileValidation';

interface UseFileUploadOptions {
  config?: FileUploadConfig;
  onUploadComplete?: (files: UploadedFile[]) => void;
  onError?: (errors: FileValidationError[]) => void;
}

export const useFileUpload = (options: UseFileUploadOptions = {}) => {
  const { config = DEFAULT_FILE_CONFIG, onUploadComplete, onError } = options;
  
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState<FileValidationError[]>([]);

  const addFiles = useCallback((files: File[]) => {
    // Create validation context
    const context: ValidationContext = {
      existingFiles: uploadedFiles.map(f => f.file),
      config
    };
    
    // Validate files with context
    const validationResult = validateFilesWithContext(files, context);
    
    if (!validationResult.valid) {
      setErrors(validationResult.errors);
      onError?.(validationResult.errors);
      return;
    }

    // Add accepted files
    const newUploadedFiles: UploadedFile[] = validationResult.acceptedFiles.map(file => ({
      id: generateFileId(),
      file,
      status: 'pending' as const
    }));

    setUploadedFiles(prev => [...prev, ...newUploadedFiles]);
    setErrors([]);
  }, [uploadedFiles, config, onError]);

  const removeFile = useCallback((fileId: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);

  const clearFiles = useCallback(() => {
    setUploadedFiles([]);
    setErrors([]);
  }, []);

  const startUpload = useCallback(async () => {
    if (uploadedFiles.length === 0) return;

    setIsProcessing(true);
    
    try {
      // ファイルのステータスを'uploading'に更新
      setUploadedFiles(prev => 
        prev.map(file => ({ ...file, status: 'uploading' as const, progress: 0 }))
      );

      // TODO: 実際のAPIコールに置き換える
      // const response = await uploadFilesToAPI(uploadedFiles.map(f => f.file));
      
      // シミュレーション
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // アップロード完了
      const completedFiles = uploadedFiles.map(file => ({ 
        ...file, 
        status: 'completed' as const, 
        progress: 100 
      }));
      
      setUploadedFiles(completedFiles);
      onUploadComplete?.(completedFiles);
      
    } catch (error) {
      // エラーハンドリング
      setUploadedFiles(prev => 
        prev.map(file => ({ 
          ...file, 
          status: 'error' as const, 
          error: error instanceof Error ? error.message : '不明なエラーが発生しました'
        }))
      );
      
      const uploadError: FileValidationError = {
        type: 'unknown',
        fileName: '',
        message: 'アップロードに失敗しました'
      };
      setErrors([uploadError]);
      onError?.([uploadError]);
      
    } finally {
      setIsProcessing(false);
    }
  }, [uploadedFiles, onUploadComplete, onError]);

  const handleDragEvents = {
    onDragOver: useCallback((e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(true);
    }, []),
    
    onDragLeave: useCallback((e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
    }, []),
    
    onDrop: useCallback((e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      
      const files = Array.from(e.dataTransfer.files);
      addFiles(files);
    }, [addFiles])
  };

  return {
    uploadedFiles,
    isDragging,
    isProcessing,
    errors,
    addFiles,
    removeFile,
    clearFiles,
    startUpload,
    handleDragEvents,
    canUpload: uploadedFiles.length > 0 && !isProcessing
  };
};